import Box from "@/common/components/Box";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import ResumeItem from "@/common/components/ResumeItem";
import { cn } from "@/common/utils/cn";
import { formatDateTime } from "@/common/utils/formatters";
import {
  MaintenanceForwardingSectionStyle,
  maintenanceForwardingSectionStyles,
} from "@/features/maintenance/features/workflows/constants/forwardings/maintenanceForwardingSectionStyles.constant";
import { checkoutEntryOptions } from "@/features/maintenance/features/workflows/constants/forwardings/steps/checkoutEntry/checkoutEntryOptions.constant";
import { transportAutoEntryStatusLabels } from "@/features/maintenance/features/workflows/constants/forwardings/transportAutoEntry/transportAutoEntryStatusLabels.constant";
import { useForwardingTabContext } from "@/features/maintenance/features/workflows/hooks/forwardings/useForwardingTabContext";
import { MaintenanceForwardingStatus } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";
import { useMemo } from "react";
import CompleteCheckoutEntry from "./CompleteCheckoutEntry";

const MaintenanceWorkflowForwardingCheckoutEntrySection = () => {
  const { forwarding, forwardingUpdatedAt } = useForwardingTabContext();

  const sectionStyle = useMemo(() => {
    if (forwarding?.checkout_entry?.completed_by)
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Completed];

    if (forwarding?.status === MaintenanceForwardingStatus.OnGoingCheckout)
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Pending];

    return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Default];
  }, [forwardingUpdatedAt]);

  if (forwarding.status === MaintenanceForwardingStatus.Canceled && !forwarding?.checkout_entry)
    return null;

  return (
    <Box className={cn('p-4 flex flex-col gap-4', sectionStyle)}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">
            Método de checkout
          </p>

          {forwarding?.checkout_entry?.completed_by && (
            <div className="flex items-center gap-1 -mb-1">
              <p className="text-valid text-xs">
                Check-out concluído
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {!forwarding.checkout_entry && (
            <p>
              Aguardando definição do cliente
            </p>
          )}

          {forwarding.checkout_entry?.status === 'in_transit' && (
            <CompleteCheckoutEntry />
          )}
        </div>
      </div>

      {forwarding.checkout_entry && (
        <div className="flex gap-4">
          {forwarding.checkout_entry.photos && forwarding.checkout_entry.photos.length > 0 && (
            <ImageCarousel
              imageClassName="object-contain"
              className="size-52"
              files={forwarding.checkout_entry.photos.map((file) => ({ id: file.id, file: file.url }))}
              showRemoveButton={false}
              showExpandButton
            />
          )}

          <div className="flex flex-col gap-3">
            <div className="flex gap-4">
              {forwarding?.checkout_entry?.status && (
                <ResumeItem
                  label="Status"
                  value={transportAutoEntryStatusLabels[forwarding.checkout_entry.status] || forwarding.checkout_entry.status}
                  className="text-xs"
                  valueClassName="font-bold text-sm"
                />
              )}

              {forwarding?.checkout_entry_type && (
                <ResumeItem
                  label="Tipo de checkout"
                  value={checkoutEntryOptions[forwarding.checkout_entry_type]?.title || '--'}
                  className="text-xs"
                  valueClassName="font-bold text-sm"
                />
              )}

              {forwarding?.checkout_entry?.responsible && (
                <ResumeItem
                  label="Responsável por transporte"
                  value={forwarding?.checkout_entry?.responsible.full_name || '--'}
                  className="text-xs"
                  valueClassName="font-bold text-sm"
                />
              )}
            </div>

            {forwarding?.checkout_entry?.completed_at && (
              <div className="flex gap-4">
                <ResumeItem
                  label="Check-out realizado"
                  value={formatDateTime(new Date(forwarding?.checkout_entry?.completed_at))}
                  className="text-xs"
                  valueClassName="font-bold text-sm"
                />

                {forwarding?.checkout_entry?.completed_by && (
                  <ResumeItem
                    label="Responsável que realizou o check-out"
                    value={forwarding.checkout_entry.completed_by.full_name}
                    className="text-xs"
                    valueClassName="font-bold text-sm"
                  />
                )}
              </div>
            )}

            {forwarding?.checkout_entry?.observations && (
              <ResumeItem
                label='Observações de check-out'
                value={forwarding?.checkout_entry.observations || '--'}
                valueClassName='text-sm'
              />
            )}
          </div>
        </div>
      )}
    </Box>
  )
}

export default MaintenanceWorkflowForwardingCheckoutEntrySection;
